import { useAuth0 } from "@auth0/auth0-react";
import {
  Typography,
  Button,
  ButtonGroup,
  Container,
  Box,
  Alert,
} from "@mui/material";
import { useState, useEffect, useMemo } from "react";
import { ErrorBoundary } from "react-error-boundary";
import { useNavigate } from "react-router";

import API_Calls_Preview from "@/api/calls/API_Calls_Preview";
import API_Calls_Template from "@/api/calls/API_Calls_Template";
import TemplateNameModal from "@/components/membership/Modals/TemplateNameModal";
import CircularLoaderWithPercentage from "@/components/membership/Template/CircularLoaderWithPercentage";
import TemplateErrorsModal from "@/components/membership/Template/TemplateErrorsModal";
import TemplateFallback from "@/components/membership/Template/TemplateFallback";
import TemplateMapper from "@/components/membership/Template/TemplateMapper";
import TemplateSidebar from "@/components/membership/Template/TemplateSidebar";
import {
  formatErrorNotification,
  getInvalidColumnData,
  templateHasChanged,
} from "@/components/membership/Template/utils/templateUtils";
import Notifications from "@/components/Notifications/Notifications";
import CatalistOnlyChip from "@/components/sharedComponents/CatalistOnlyChip";
import { MB_PER_SECOND } from "@/constants/preview";
import { useUpdateDataContext } from "@/contexts/UpdateDataContext";
import useSaveTemplateHandler from "@/hooks/useSaveTemplateHandler";
import useTemplateNamesHandler from "@/hooks/useTemplateNamesHandler";
import "@/components/membership/Template/TemplateEditorStyles.css";

function TemplateEditor() {
  const navigate = useNavigate();
  const { user } = useAuth0();
  const { templateData, affiliateFileData, handleSummaryDataChange } =
    useUpdateDataContext();
  const [updatedTemplate, setUpdatedTemplate] = useState(templateData.template);
  const [previewProcessing, setPreviewProcessing] = useState(false);
  const [secondsToProcess, setSecondsToProcess] = useState(0);
  const [isPreviewable, setIsPreviewable] = useState(true);
  const [notification, setNotification] = useState({});
  const [templateErrorsModalOpen, setTemplateErrorsModalOpen] = useState(false);
  const [templateMappingErrors, setTemplateMappingErrors] = useState({});
  const { handleSaveTemplate } = useSaveTemplateHandler();
  const { processTemplateMapping } = API_Calls_Template();
  const { getPreviewSummary } = API_Calls_Preview();
  const { getDisplayName } = useTemplateNamesHandler();

  const storeTemplate = useMemo(
    () => JSON.stringify(templateData.template),
    [templateData]
  );

  useEffect(() => {
    const invalidColumnData = getInvalidColumnData(
      templateData.template,
      affiliateFileData.columns
    );
    if (invalidColumnData.length) {
      setTemplateErrorsModalOpen(true);
      setTemplateMappingErrors({
        errors: invalidColumnData,
      });
      setIsPreviewable(false);
    } else {
      setIsPreviewable(true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [updatedTemplate]);

  useEffect(() => {
    if (templateData.problems.length) {
      setNotification({
        title: "The following fields have been deprecated in Ada.",
        message: formatErrorNotification(
          templateData.problems,
          "Please remap your data to currently available fields."
        ),
        severity: "error",
        duration: null, // notification will stay open until the user closes it
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleUpdatedTemplate = (updatedTemplate) =>
    setUpdatedTemplate(updatedTemplate);

  const handleSaveClick = async () => {
    const { isSuccess, saveResponse } = await handleSaveTemplate({
      templateName: templateData.name,
      templateHasChanged: templateHasChanged(storeTemplate, updatedTemplate),
      templateAlreadyExists: !!templateData.created,
      isPreviewSave: false,
      use: templateData.use,
    });
    if (isSuccess) {
      setSuccessNotification();
    } else {
      setErrorNotification(saveResponse);
    }
  };

  const handleRenameClick = async (newName) => {
    await handleSaveTemplate({
      templateName: newName,
      templateHasChanged: true,
      templateAlreadyExists: true,
      isPreviewSave: false,
      use: templateData.use,
    });
  };

  const handleSaveAsNew = async (name) => {
    const isSuccess = await handleSaveTemplate({
      templateName: name,
      templateHasChanged: true,
      templateAlreadyExists: false,
      isPreviewSave: false,
      use: templateData.use,
    });
    if (isSuccess) {
      setSuccessNotification();
    }
  };

  const handlePreviewClick = async () => {
    setPreviewProcessing(true);

    // save template before processing
    const { isSuccess, saveResponse } = await handleSaveTemplate({
      templateName: templateData.name,
      templateHasChanged: templateHasChanged(storeTemplate, updatedTemplate),
      templateAlreadyExists: !!templateData.created,
      isPreviewSave: true,
      use: templateData.use,
    });

    if (isSuccess) {
      const processResponse = await processTemplateMapping(
        saveResponse.affiliate,
        saveResponse,
        affiliateFileData,
        templateData.use
      );

      if (processResponse.error) {
        setErrorNotification(processResponse);
        setPreviewProcessing(false);
        return;
      }

      const processTimestamp = processResponse.data.timestamp;
      const adaFile = `${processTimestamp}/${affiliateFileData.name}@${affiliateFileData.created}/${saveResponse.name}@${saveResponse.created}`;
      const previewResponse = await getPreviewSummary(
        saveResponse.affiliate,
        adaFile
      );
      if (previewResponse.error) {
        console.error(
          "Error fetching preview summary:",
          previewResponse.message || "Unknown error"
        );
        return;
      }

      handleSummaryDataChange({
        summary: previewResponse.data,
        processTimestamp,
        processedFileAffiliate: user.org_name,
        processedDuration: processResponse.data.process_duration,
        droppedRows: processResponse.data.dropped_rows,
        fullPath: processResponse.data.full_path,
      });
      navigate("/membership/update-data/preview-file");
    } else {
      setErrorNotification(saveResponse);
      setPreviewProcessing(false);
    }
  };

  const estimatePreviewProcessingTime = () => {
    const fileSize = affiliateFileData.size;
    const tempSecondsToProcess = fileSize / MB_PER_SECOND;
    setSecondsToProcess(tempSecondsToProcess);
  };

  useEffect(() => {
    estimatePreviewProcessingTime();
    // estimatePreviewProcessingTime is an inline function recreated every
    // render; depending on it would cause infinite re-runs.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [affiliateFileData]);

  const setSuccessNotification = (r) => {
    setNotification({
      message: "Template saved successfully",
      severity: "success",
      duration: 3000,
    });
  };

  const setErrorNotification = (r) => {
    if (r.status === 400) {
      setNotification({
        title: r.message,
        message: formatErrorNotification(r.details),
        severity: "error",
        duration: null, // notification will stay open until the user closes it
      });
    } else {
      setNotification({
        message: "Unable to process at this time.",
        severity: "error",
        duration: 5000,
      });
    }
  };

  return (
    <>
      <Container
        className="template-editor-container"
        data-testid="template-editor"
      >
        {previewProcessing ? (
          <Box className="spinner-container">
            <Alert severity="warning" sx={{ marginBottom: 5 }}>
              Please do not refresh this page while it is processing!
            </Alert>
            <CircularLoaderWithPercentage totalSeconds={secondsToProcess} />
          </Box>
        ) : (
          <>
            <ErrorBoundary FallbackComponent={TemplateFallback}>
              <Box className="template-editor-titleAndButtons">
                <Box sx={{ display: "flex", alignItems: "center" }}>
                  <Typography variant="h5">
                    {getDisplayName(templateData.name, templateData.created)}
                  </Typography>
                  {templateData.use === "catalist-only" && <CatalistOnlyChip />}
                </Box>

                <Box>
                  <ButtonGroup>
                    <Button
                      className="siteBlueOutlined"
                      onClick={handleSaveClick}
                      data-testid="save-template"
                    >
                      Save
                    </Button>
                    <TemplateNameModal
                      title={"Save As New"}
                      message="Enter a name for the new template being created"
                      handleSubmit={handleSaveAsNew}
                      data-testid="save-as-new-template"
                    />
                    <TemplateNameModal
                      title={"Rename"}
                      message="Enter a new name for the template"
                      handleSubmit={handleRenameClick}
                      data-testid="rename-template"
                    />
                  </ButtonGroup>

                  <Button
                    sx={{ marginLeft: "10px" }}
                    className="siteBlue"
                    disabled={!isPreviewable}
                    onClick={handlePreviewClick}
                    data-testid="preview-template"
                  >
                    Preview
                  </Button>
                </Box>
              </Box>
              <Box
                sx={{
                  display: "flex",
                  marginTop: "1rem",
                  marginBottom: "2rem",
                }}
              >
                <TemplateSidebar template={templateData.template} />
                <TemplateMapper
                  handleUpdatedTemplate={handleUpdatedTemplate}
                  initialTemplateData={templateData.template}
                />
              </Box>
            </ErrorBoundary>
          </>
        )}
      </Container>
      <TemplateErrorsModal
        isOpen={templateErrorsModalOpen}
        onClose={() => setTemplateErrorsModalOpen(false)}
        errors={templateMappingErrors}
      />
      {!!Object.keys(notification).length && (
        <Notifications
          notification={notification}
          setNotification={setNotification}
        />
      )}
    </>
  );
}

export default TemplateEditor;
